import { assert } from '@/core'
import { BaseService } from './base.service'
import { ResponseWrapper } from './util'
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
export class PatientTimeSlotsService extends BaseService {

  static get entity() {
    return 'patient-time-slots'; // Update to reflect correct entity for the time slots
  }

  static async getList(queueSide, parameters = {}) {
    assert.object(parameters);
    const params = { ...parameters };
    let url = queueSide ? "/patient-time-slots/kiosk/queue-counter" : "/patient-time-slots/kiosk/list";

    const response = await this.request({ auth: true }).get(url, { params });

    // Current local machine time
    const now = dayjs();

    // Define status priority order based on queueSide
    const statusOrder = queueSide
      ? { waiting: 1, started: 2, inprogress: 3, completed: 4, cancelled: 5 }
      : { started: 1, inprogress: 2, waiting: 3, completed: 4, cancelled: 5 };

    // Process the response data
    let processedContent = response.data.data.map(appointment => {
      // Convert startTime from UTC to local time
      const startTime = dayjs.utc(appointment?.slots?.startTime).local();
      const endTime = appointment?.slots?.endTime ? dayjs.utc(appointment.slots.endTime).local() : null;

      // Calculate the difference in minutes
      let diffInMinutes = startTime.diff(now, 'minute');

      // Ensure no negative values
      diffInMinutes = Math.max(0, diffInMinutes);

      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;

      return {
        ...appointment,
        estimatedTime: diffInMinutes, // Total minutes
        humanReadableEstimatedTime: hours > 0 
        ? `${hours}h ${minutes}m` 
        : `${minutes} mins`, // Show hours only if > 0        formattedStartTime: startTime.format('YYYY-MM-DD HH:mm:ss'), // Local time formatted
        statusPriority: statusOrder[appointment.status] ?? 6, // Default to lowest priority if unknown status
        endTime
      };
    });

    // Handle status updates when `authQueue` is not present or false
    const authQueue = localStorage.getItem('autoQueueMainage');
    if (!authQueue) {
      const excludedAppointments = processedContent.filter(appointment => appointment.estimatedTime === 0);

      // Prepare status updates
      const statusUpdates = excludedAppointments.map(appointment => {
        let newStatus = null;
        switch (appointment.status) {
          case "waiting":
            newStatus = "started";
            break;
          case "started":
            newStatus = "inprogress";
            break;
          case "inprogress":
            if (appointment.endTime && appointment.endTime.isBefore(now)) {
              newStatus = "completed";
            }
            break;
        }
        return newStatus ? { patientTimeSlotId: appointment.id, status: newStatus } : null;
      }).filter(update => update !== null);

      if (statusUpdates.length > 0) {
        try {
          // Ensure bulk update API request is awaited
          const response = await PatientTimeSlotsService.bulkUpdate(statusUpdates);
          if (response.success) {
            // console.log("✅ Bulk update successful:", response.message);

            // Update the status of processed appointments instead of removing them
            processedContent = processedContent.map(appointment => {
              const updatedAppointment = statusUpdates.find(updated => updated.patientTimeSlotId === appointment.id);
              return updatedAppointment
                ? { ...appointment, status: updatedAppointment.status }
                : appointment;
            });

          }
        } catch (error) {
          console.error("❌ Failed to update appointments:", error);
        }
      }
    }

    // Remove appointments with estimatedTime 0 if not on queueSide
    if (!queueSide) {
      processedContent = processedContent.filter(
        appointment => appointment.estimatedTime > 0 || appointment.status === "started"
      );
    }


    // Sort first by statusPriority, then by estimatedTime
    processedContent.sort((a, b) => {
      if (queueSide) {
        return a.statusPriority !== b.statusPriority
          ? a.statusPriority - b.statusPriority
          : a.estimatedTime - b.estimatedTime;
      }
      return a.estimatedTime - b.estimatedTime;
    });

    return new ResponseWrapper(response, {
      content: processedContent,
      total: Number(response.headers['x-total-count']),
    });
  }




  static async getById(id) {
    assert.string(id, { required: true })

    const response = await this.request({ auth: true }).get(`${this.entity}/${id}`)
    return new ResponseWrapper(response, response.data.data)

  }

  static async create(data = {}) {
    assert.object(data, { required: true })

    const response = await this.request({ auth: true }).post(`/manual/patient-time-slots`, data)
    return new ResponseWrapper(response, response.data.data)

  }

  static async update(id, data = {}) {
    assert.string(id, { required: true })
    assert.object(data, { required: true })

    const response = await this.request({ auth: true }).patch(`patient-time-slots/patient/${id}`, data)
    return new ResponseWrapper(response, response.data.data)

  }
  static async bulkUpdate(data) {

    const response = await this.request({ auth: true }).patch(`/bulk/patient-time-slots`, data)
    return new ResponseWrapper(response, response.data.data)

  }
}
